/**
 * Le client HTTP qui **renouvelle** la session au lieu de la laisser mourir
 * (dette `D-30`, troisième surface).
 *
 * Jusqu'ici, un `401` arrivait au bout de l'heure et l'écran n'avait rien d'autre à
 * offrir que l'écran de connexion — mot de passe **et** code reçu par SMS. Le serveur
 * sert désormais `POST /api/backoffice/session/refresh` ; ce module est ce qui
 * l'utilise, et sans lui la route serait exactement ce que `D-30` reproche : une
 * moitié émettrice que personne ne consomme.
 *
 * ## Deux moments, et pas un de plus
 *
 * 1. **Avant l'appel**, si le jeton d'accès arrive à échéance : c'est le
 *    renouvellement *à temps*. Après coup, il est trop tard — le serveur révoque la
 *    session à l'instant où il refuse le jeton périmé, et il n'y a plus rien à
 *    renouveler.
 * 2. **Sur un `401`**, une seule fois : le filet. Une horloge de poste décalée, un
 *    renouvellement perdu, une réponse plus lente que l'échéance. Si l'échange échoue
 *    à son tour, la session est finie et l'écran le dit.
 *
 * ## Un `403` n'est PAS un `401`
 *
 * Le client HTTP a longtemps rendu la même nature pour les deux, et ce module rejouait
 * donc chaque **refus d'habilitation** : une rotation du couple de jetons, puis un
 * second appel refusé exactement comme le premier. Un module que l'opérateur n'a pas le
 * droit d'ouvrir faisait ainsi tourner sa session à chaque visite, et doublait le
 * nombre de refus écrits au journal d'audit. Aucune reconnexion n'accorde un droit :
 * seul `unauthorized` déclenche l'échange, et `forbidden` remonte tel quel.
 *
 * ## Pourquoi PAS de minuteur d'arrière-plan
 *
 * Un minuteur qui rafraîchirait toutes les *n* minutes rafraîchirait aussi un poste
 * que personne ne regarde. Le serveur refuse déjà ce cas — il exige que la session ne
 * soit pas inactive, et il reporte la dernière activité d'une rotation à l'autre —, si
 * bien qu'un tel minuteur ne prolongerait rien ; il produirait seulement des refus
 * réguliers sur un poste abandonné. **Le renouvellement est donc porté par l'activité
 * elle-même** : il n'a lieu que parce qu'un agent demande quelque chose.
 *
 * ## Un seul échange à la fois
 *
 * Le serveur traite un jeton d'échange présenté deux fois comme un **vol** : il fait
 * tomber toutes les sessions de l'agent et écrit une alerte. Deux appels qui
 * recevraient `401` en même temps enverraient deux fois le même jeton — et
 * déconnecteraient l'agent en criant au vol sur son propre travail. Les appelants
 * partagent donc **la même promesse** d'échange ; le second attend le premier et
 * repart avec le jeton qu'il a obtenu.
 */

import { ApiError } from './api-client.js';
import type { ApiBytes, ApiClient } from './api-client.js';
import { backOfficeRefreshPath, refreshBackOfficeSession } from './back-office-session.js';
import type { OperatorSession } from '../state/session-store.js';
import type { SessionStore } from '../state/session-store.js';

/**
 * La marge devant l'échéance du jeton d'accès.
 *
 * Soixante secondes : de quoi couvrir le trajet d'un appel et le décalage d'horloge
 * ordinaire d'un poste de bureau, sans renouveler à tout bout de champ. Une marge nulle
 * ferait courir chaque renouvellement contre l'échéance qu'il vise.
 */
export const renewalMarginMs = 60_000;

/** Ce qu'il faut pour renouveler. */
export interface SessionRenewalOptions {
  /** Le transport nu. C'est lui qui pose l'en-tête et compte les corrélations. */
  readonly client: ApiClient;
  /** Le magasin : d'où vient le couple de jetons, et où va le neuf. */
  readonly store: SessionStore;
  /** L'horloge, remplaçable par un test. */
  readonly now?: () => number;
  /** La marge devant l'échéance, en millisecondes. */
  readonly marginMs?: number;
}

/**
 * Enveloppe [options.client] d'un renouvellement de session.
 *
 * Rend un `ApiClient` : tout ce qui appelle le serveur passe par la même interface
 * qu'avant, et aucun écran n'a à savoir que la session se renouvelle. Poser le
 * renouvellement dans chaque appel métier en aurait fait une discipline — et le premier
 * appel écrit sans elle aurait déconnecté l'agent sans que personne comprenne pourquoi.
 */
export function createRenewingApiClient(options: SessionRenewalOptions): ApiClient {
  const maintenant = options.now ?? (() => Date.now());
  const marge = options.marginMs ?? renewalMarginMs;
  const { client, store } = options;

  /** L'échange en cours, partagé par tous les appelants. Voir l'en-tête. */
  let echangeEnCours: Promise<OperatorSession | null> | null = null;

  /**
   * Échange le jeton, une seule fois à la fois.
   *
   * Rend la session neuve, ou `null` si l'échange a été refusé — auquel cas la session
   * de travail est close et l'écran repart à `B-01`.
   */
  function echanger(): Promise<OperatorSession | null> {
    if (echangeEnCours !== null) return echangeEnCours;

    const courante = store.getState().operator;
    if (courante === null) return Promise.resolve(null);

    echangeEnCours = refreshBackOfficeSession(client, courante.refreshToken)
      .then((neuve) => {
        store.getState().renewSession(neuve);
        return neuve;
      })
      .catch(() => {
        // Un échange refusé n'est pas un incident à réessayer : le serveur vient de
        // dire que cette session est finie — périmée, inactive au-delà du délai, ou
        // révoquée. Insister ferait rejouer un jeton mort, c'est-à-dire déclencher
        // l'alerte de vol du serveur sur un agent honnête.
        store.getState().expireSession();
        return null;
      })
      .finally(() => {
        echangeEnCours = null;
      });

    return echangeEnCours;
  }

  /** Le jeton d'accès arrive-t-il à échéance ? */
  function bientotPerime(session: OperatorSession): boolean {
    const echeance = Date.parse(session.expiresAt);
    // Une date illisible ne déclenche PAS de renouvellement : elle ferait échanger le
    // jeton à chaque appel, et chaque échange est une rotation de plus à payer. Le
    // filet du `401` reste, lui, en place.
    if (Number.isNaN(echeance)) return false;
    return maintenant() >= echeance - marge;
  }

  /**
   * Les deux moments du renouvellement, autour d'un appel quelconque.
   *
   * Écrit une fois et partagé par les deux lectures : une image de dossier est
   * une lecture comme une autre, et la laisser hors du renouvellement aurait
   * fait échouer précisément l'écran où l'agent reste le plus longtemps — la
   * revue, dont la session dure autant que sa file.
   */
  async function avecRenouvellement<T>(
    path: string,
    appeler: () => Promise<T>,
  ): Promise<T> {
    // L'échange lui-même ne se renouvelle pas : ce serait une récursion, et le
    // seul jeton qu'il porte est justement celui qu'on échange.
    if (path === backOfficeRefreshPath) return appeler();

    const courante = store.getState().operator;
    if (courante !== null && bientotPerime(courante)) {
      await echanger();
    }

    try {
      return await appeler();
    } catch (echec) {
      // Une seule reprise, et seulement sur une session refusée — jamais sur un
      // droit manquant, qu'aucun jeton neuf n'accorde (voir l'en-tête). Boucler sur
      // un `401` persistant enverrait l'agent dans une file d'échanges dont chacun
      // rejouerait le précédent.
      if (!(echec instanceof ApiError) || echec.kind !== 'unauthorized') throw echec;
      if (store.getState().operator === null) throw echec;

      const renouvelee = await echanger();
      if (renouvelee === null) throw echec;
      return await appeler();
    }
  }

  return {
    get lastCorrelationId(): string | null {
      return client.lastCorrelationId;
    },
    async request<T>(path: string, init: RequestInit = {}): Promise<T> {
      return avecRenouvellement(path, () => client.request<T>(path, init));
    },
    async requestBytes(path: string, init: RequestInit = {}): Promise<ApiBytes> {
      return avecRenouvellement(path, () => client.requestBytes(path, init));
    },
  };
}
